import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { programs } from "@/mocks/programs";
import { videos } from "@/mocks/videos";
import { useTranslatedList } from "@/hooks/useTranslatedList";
import Reveal from "@/components/base/Reveal";

export default function ProgramDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const foundProgram = programs.find((p) => p.slug === slug);
  const program = useTranslatedList(foundProgram ? [foundProgram] : [], [
    "name",
    "description",
    "category",
    "department",
  ])[0];
  const relatedVideos = useTranslatedList(
    videos
      .filter(
        (v) =>
          v.category === foundProgram?.category ||
          v.tags.some((t) => foundProgram?.tags.includes(t)),
      )
      .slice(0, 3),
    ["title"],
  );

  useEffect(() => {
    if (program) {
      document.title = `${program.name} | L+TV Programs`;
    }
  }, [program]);

  if (!program) {
    return (
      <div className="pt-20 ltv-container py-16 text-center">
        <i className="ri-error-warning-line text-4xl text-foreground-300 mb-3"></i>
        <h1 className="text-xl font-bold text-foreground-800 mb-2">{t("program.notFound")}</h1>
        <Link to="/programs" className="ltv-btn-primary text-sm inline-flex items-center gap-2">
          <i className="ri-arrow-left-line"></i>
          {t("program.backToPrograms")}
        </Link>
      </div>
    );
  }

  const dayMap: Record<string, string> = {
    Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu",
    Friday: "Fri", Saturday: "Sat", Sunday: "Sun",
  };

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <div className="flex items-center gap-2 mb-2 text-sm text-white/60">
            <Link to="/programs" className="hover:text-white transition-colors">{t("nav.programs")}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>{program.name}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">{program.name}</h1>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Reveal direction="up">
              <div className="rounded-lg overflow-hidden mb-6">
                <img src={program.image} alt={program.name} className="w-full aspect-video object-cover" />
              </div>
              <div className="prose max-w-none text-foreground-700 leading-relaxed mb-8">
                <p>{program.description}</p>
              </div>
            </Reveal>

            {relatedVideos.length > 0 && (
              <Reveal direction="up">
                <div className="border-t border-background-200 pt-8">
                  <h3 className="text-lg font-bold text-foreground-900 mb-4">{t("program.relatedVideos")}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedVideos.map((v) => (
                      <Link key={v.id} to={`/videos/${v.slug}`} className="group flex gap-3">
                        <div className="relative w-32 h-20 flex-shrink-0 rounded-md overflow-hidden">
                          <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 rounded">{v.duration}</div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-accent-600 transition-colors">{v.title}</p>
                          <p className="text-xs text-foreground-500 mt-1">{v.views} {t("program.views")}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-1">
            <Reveal direction="left" delay={100}>
              <div className="bg-white rounded-lg border border-background-200 p-5">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-500 mb-4">{t("program.programDetails")}</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t("program.category")}</p>
                    <p className="text-sm font-medium text-foreground-800">{program.category}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t("program.department")}</p>
                    <p className="text-sm font-medium text-foreground-800">{program.department}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t("program.presenter")}</p>
                    <p className="text-sm font-medium text-foreground-800">{program.presenter}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t("program.broadcastDays")}</p>
                    <div className="flex flex-wrap gap-1">
                      {program.broadcastDays.map((d) => (
                        <span key={d} className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">{dayMap[d] || d}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t("program.time")}</p>
                    <p className="text-sm font-medium text-foreground-800">{program.broadcastTime}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t("program.duration")}</p>
                    <p className="text-sm font-medium text-foreground-800">{program.duration}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}